import { ConnectFreeSeatingResponse, IDRole, RiderReserver, Rights, User } from '../redux/reducers/api-types';
import features from '../features/features';

const LOCAL_STORAGE_KEY = 'user';
export const LOCAL_STORAGE_TABLE_DENSE = 'tableDense';
export const LOCAL_STORAGE_TABLE_COLUMNS = 'tableColumns';
export const LOCAL_STORAGE_VIADIALOG = 'viadialog';
export const LOCAL_STORAGE_IMPERSONATE = 'impersonate';
export const LOCAL_STORAGE_EVENT = 'event';
export const LOCAL_STORAGE_RECENT_PAGES = 'recentPages';

export enum AccountType {
    Admin = 'admin',
    Operator = 'operator',
}

export enum AccountRole {
    God = 'GOD',
    Intern = 'ORIGIN_SCLC',
    Admin_ID = 'INT_ADMIN_ID',
    Ops = 'INT_OPS',
    Impossible = 'LKJZELK',
}

export enum AccountRight {
    RIDE_VIEW = 'RIDE_VIEW',
    RIDE_DETAILS = 'RIDE_DETAILS',
    RIDE_ACTION_ADJUSTMENT = 'RIDE_ACTION_ADJUSTMENT',
    RIDE_ACTION_CREATE = 'RIDE_ACTION_CREATE',
    RIDE_ACTION_CANCEL = 'RIDE_ACTION_CANCEL',
    //BILLING
    PARTNER_BILLING_MGMT = 'PARTNER_BILLING_MGMT',
    RIDER_BILLING_MGMT = 'RIDER_BILLING_MGMT',
    RIDER_BILLING_EXPORT = 'RIDER_BILLING_EXPORT',
    RIDER_BILLING_PYMT_RETRY = 'RIDER_BILLING_PYMT_RETRY',
    EXCEPTIONAL_BILLING_MGMT = 'EXCEPTIONAL_BILLING_MGMT',
    CREDIT_NOTE_MGMT = 'CREDIT_NOTE_MGMT',
    BILL_RECTIFICATION = 'BILL_RECTIFICATION',
    //PRICING
    PRICING_MGMT = 'PRICING_MGMT',
    //COMPANY
    COMPANY_PRICING = 'COMPANY_PRICING',
    COMPANY_FID_PROGRAM = 'COMPANY_FID_PROGRAM',
    COMPANY_SETTINGS = 'COMPANY_SETTINGS',
    COMPANY_EXPORT = 'COMPANY_EXPORT',
    //RIDER
    RIDER_PROFILE = 'RIDER_PROFILE',
    //PARTNER / DRIVER / VEHICLE
    PARTNER_SETTINGS = 'PARTNER_SETTINGS',
    PARTNER_BILLING = 'PARTNER_BILLING',
    DRIVER_PREBOOK = 'DRIVER_PREBOOK',
    //MARKETING
    PROMO_VIEW = 'PROMO_VIEW',
    PROMO_DETAILS = 'PROMO_DETAILS',
    PUSH_NOTIFICATION_DRIVER = 'PUSH_NOTIFICATION_DRIVER',
    PUSH_NOTIFICATION_RIDER = 'PUSH_NOTIFICATION_RIDER',
    START_MESSAGE_DRIVER = 'START_MESSAGE_DRIVER',
    START_MESSAGE_RIDER = 'START_MESSAGE_RIDER',
    REWARD_B2C = 'REWARD_B2C',
    //CABCOM
    CABCOM_MGMT = 'CABCOM_MGMT',
    CABCOM_USE = 'CABCOM_USE',
    //IMPERSONNATION
    SEC_IMPERSONATE = 'SEC_IMPERSONATE',
    SEC_IMPERSONATE_EXTENDED = 'SEC_IMPERSONATE_EXTENDED',
    //ADMIN
    SEC_ADMIN_USERS = 'SEC_ADMIN_USERS',
    //OPERATIONS
    RIDE_ACTION_IMPORT = 'RIDE_ACTION_IMPORT',
}

export interface RightDef {
    name: string;
    mode?: RightRank;
}

export enum RightRank {
    R = 1,
    W = 2,
}

export interface ImpersonateData {
    url: string;
    rider?: RiderReserver;
}

export default class UserUtils {
    //=======================================//
    //=============== GENERIC ===============//
    //=======================================//

    static setPref(key, obj) {
        localStorage.setItem(key, JSON.stringify(obj));
    }
    static getPref(key) {
        return JSON.parse(localStorage.getItem(key));
    }
    static removePref(key) {
        return localStorage.removeItem(key);
    }

    //=============================================//
    //=============== Table Columns ===============//
    //=============================================//

    static getTableColumns() {
        return UserUtils.getPref(LOCAL_STORAGE_TABLE_COLUMNS);
    }

    static getTableColumnsForTable(table: string) {
        return (UserUtils.getPref(LOCAL_STORAGE_TABLE_COLUMNS) || {})[table];
    }

    static setTableColumns(table: string, column: string, value: boolean) {
        const tablesColumns = this.getTableColumns() || {};

        if (!tablesColumns[table]) {
            tablesColumns[table] = {};
        }
        tablesColumns[table][column] = value;

        UserUtils.setPref(LOCAL_STORAGE_TABLE_COLUMNS, tablesColumns);
    }

    static removeTableColumns() {
        UserUtils.removePref(LOCAL_STORAGE_TABLE_COLUMNS);
    }

    //=========================================//
    //=============== Viadialog ===============//
    //=========================================//

    static getFreeSeatingInfos() {
        return UserUtils.getPref(LOCAL_STORAGE_VIADIALOG);
    }

    static setFreeSeatingInfos(infos: ConnectFreeSeatingResponse) {
        UserUtils.setPref(LOCAL_STORAGE_VIADIALOG, infos);
    }

    static removeFreeSeatingInfos() {
        UserUtils.removePref(LOCAL_STORAGE_VIADIALOG);
    }

    //===========================================//
    //=============== Impersonate ===============//
    //===========================================//

    static getImpersonateInfos(): ImpersonateData {
        return UserUtils.getPref(LOCAL_STORAGE_IMPERSONATE);
    }

    static setImpersonateInfos(url: string, rider?: RiderReserver) {
        const payload: ImpersonateData = { url };
        if (rider) {
            payload.rider = rider;
        }
        UserUtils.setPref(LOCAL_STORAGE_IMPERSONATE, payload);
    }

    static removeImpersonateInfos() {
        UserUtils.removePref(LOCAL_STORAGE_IMPERSONATE);
    }

    //====================================//
    //=============== User ===============//
    //====================================//

    static getToken() {
        const user = UserUtils.getPref(LOCAL_STORAGE_KEY);
        return user?.access_token || '';
    }

    static setUser(obj) {
        UserUtils.setPref(LOCAL_STORAGE_KEY, obj);
    }

    static getUser() {
        return UserUtils.getPref(LOCAL_STORAGE_KEY);
    }

    static removeUser() {
        UserUtils.removePref(LOCAL_STORAGE_KEY);
    }

    static isAgent() {
        if (features.debug?.viadialog?.active) {
            return features.debug?.viadialog?.value;
        }
        const connectedUser = UserUtils.getUser();
        return !!connectedUser?.user?.agent;
    }

    static isAdmin() {
        const connectedUser = this.getUser();
        if (!connectedUser || !connectedUser.user) {
            return false;
        }
        return connectedUser.user.type === AccountType.Admin;
    }
    static isOperator() {
        const connectedUser = this.getUser();
        if (!connectedUser || !connectedUser.user) {
            return false;
        }
        return connectedUser.user.type === AccountType.Operator;
    }

    static hasRole(role: AccountRole) {
        const connectedUser = this.getUser();
        if (!connectedUser || !connectedUser.roles || connectedUser.roles.length < 1) {
            return false;
        }
        return !!connectedUser.roles.find((r: IDRole) => r.name === role);
    }

    static hasRoleIntern() {
        return this.hasRole(AccountRole.Intern);
    }

    static hasRoleAdminID() {
        return this.hasRole(AccountRole.Admin_ID);
    }

    static hasRoleGod() {
        return this.hasRole(AccountRole.God);
    }

    static hasRoleInternOrGod() {
        return this.hasRoleIntern() || this.hasRoleGod();
    }

    static hasRoleAdminIDOrGod() {
        return this.hasRoleAdminID() || this.hasRoleGod();
    }

    static hasRoleOpsOrGod() {
        return this.hasRole(AccountRole.Ops) || this.hasRoleGod();
    }

    static hasRoleImpossible() {
        return this.hasRole(AccountRole.Impossible);
    }

    // User Rights

    static getRights() {
        const connectedUser = this.getUser();
        if (!connectedUser || !connectedUser.rights) {
            return {};
        }
        return connectedUser.rights;
    }

    static getRoles() {
        const connectedUser = this.getUser();
        if (!connectedUser || !connectedUser.roles) {
            return {};
        }
        return connectedUser.roles;
    }

    /**
     * Check a specific right specifying the mode W or R
     * @param checkRights Right to check
     * @param mode W or R
     * @returns Boolean if has the right with the good mode
     */
    static checkRight(checkRights: string, mode: RightRank) {
        if (!features.id.active) {
            return true;
        }
        const rights = this.getRights();
        const right: string = rights?.[checkRights];
        return right ? RightRank[right] >= mode : false;
    }

    /**
     * Check the rights
     * @param checkRights Right to check
     * @returns
     */
    static checkRights(checkRights: string | RightDef | RightDef[]) {
        if (!checkRights) {
            return true;
        }
        const rights = this.getRights();
        const rightToCheck: RightDef[] =
            typeof checkRights === 'string'
                ? ([{ name: checkRights }] as RightDef[])
                : Array.isArray(checkRights)
                  ? checkRights
                  : [checkRights];
        return this.hasRights(rights, rightToCheck);
    }

    /**
     * Check the rights or Admin if feature id is not activated
     * @param checkRights Right to check
     * @returns
     */
    static checkRightsOrAdmin(checkRights: string | RightDef | RightDef[]) {
        if (!features.id.active) {
            return this.isAdmin();
        }
        return this.checkRights(checkRights);
    }

    static hasRights(rights: Rights, checkRights: RightDef[]) {
        if (!features.id.active || !checkRights || checkRights.filter((r) => r).length < 1) {
            return true;
        }

        for (let i = 0; i < checkRights.length; i++) {
            const right: RightDef = checkRights[i];
            const neededRight: RightRank = right.mode ?? RightRank.R;
            const status: string = rights?.[right.name];
            if (!status || RightRank[status] < neededRight) {
                return false;
            }
        }

        return true;
    }

    /**
     * Check if the roles given in param has all the checkRoles asked
     * @param roles list of user's role
     * @param checkRoles list of all needed roles
     * @returns boolean
     */
    static hasRoles(roles: IDRole[], checkRoles: string[]) {
        if (!features.id.active || !checkRoles || checkRoles.filter((r) => r).length < 1) {
            return true;
        }

        for (let i = 0; i < checkRoles.length; i++) {
            const role: string = checkRoles[i];
            if (!roles.find((r) => r.name === role)) {
                return false;
            }
        }
        return true;
    }
}
